import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';

// NOTE: Admin pages are an exception to ADR-019's Supabase-import allowlist.
// Per M58 plan Step 10a + RULE-225, the BetaInvitesPage reads through the
// JWT-gated `list-beta-invites` Edge Function (NOT direct `supabase.from()`).
// The user-facing RLS policy on `beta_invites` is `redeemed_by = auth.uid()
// OR redeemed_by IS NULL` — correct for end users, but it filters out invites
// redeemed by other users from the admin table view. The Edge Function uses
// the service role to bypass RLS server-side; the server-side admin check
// inside the Edge Function is the security boundary, and the client-side
// admin allowlist is a UX gate only. The page also invokes
// `generate-beta-invite` for the create-invite form.
import { supabase } from '@/services/supabase';
import type {
  AdminInviteRow,
  GenerateInviteRequest,
  GenerateInviteResponse,
} from '@/types/beta-invite';

// Static literal values for `data-admin-form-state` (per M58 plan, static-per-branch).
type AdminFormState = 'idle' | 'submitting' | 'success' | 'error';


/**
 * Compute admin-row status from raw invite row fields.
 *
 * Per consensus-reviewer C7, the AdminInviteRow status union is now
 * 'sent' | 'active' | 'expired'. The 'expired' value tracks the 90-day
 * BETA TIER lifecycle — NOT the 48-hour invite TTL — so we read
 * `beta_expires_at` instead of computing from `created_at`.
 *
 * Status precedence (matches AdminInviteRow contract in src/types/beta-invite.ts):
 *   - redeemed_at IS NULL                                                -> 'sent'
 *   - redeemed_at IS NOT NULL AND beta_expires_at > now()                -> 'active'
 *   - redeemed_at IS NOT NULL AND beta_expires_at <= now() (or NULL)     -> 'expired'
 *
 * Notes:
 *   - Pre-redemption rows past the 48h invite TTL still render as 'sent'.
 *     The admin table is for tier-lifecycle tracking, not invite-link freshness.
 *   - A row redeemed but with a missing beta_expires_at is treated as 'expired'
 *     defensively — that state should not occur in production but should not
 *     fall through to a misleading 'active'.
 */
function computeStatus(
  redeemedAt: string | null,
  betaExpiresAt: string | null,
  nowMs: number,
): AdminInviteRow['status'] {
  if (redeemedAt == null) return 'sent';
  if (betaExpiresAt == null) return 'expired';
  const expiresMs = new Date(betaExpiresAt).getTime();
  if (!Number.isFinite(expiresMs) || expiresMs <= nowMs) return 'expired';
  return 'active';
}

/**
 * Map a raw Supabase row (snake_case) into the committed `AdminInviteRow` shape.
 *
 * Supabase rows from `beta_invites` arrive in snake_case; the committed contract
 * is camelCase (matching `src/services/supabase-repository.ts` mapper conventions).
 */
interface RawBetaInviteRow {
  id: string;
  email: string;
  created_at: string;
  redeemed_at: string | null;
  redeemed_by: string | null;
  beta_expires_at: string | null;
}

function mapRow(row: RawBetaInviteRow, nowMs: number): AdminInviteRow {
  return {
    id: row.id,
    email: row.email,
    createdAt: row.created_at,
    redeemedAt: row.redeemed_at,
    redeemedBy: row.redeemed_by,
    betaExpiresAt: row.beta_expires_at,
    // C7: status now derives from beta_expires_at (90-day tier lifecycle),
    // not from created_at (48h invite TTL).
    status: computeStatus(row.redeemed_at, row.beta_expires_at, nowMs),
  };
}

export function BetaInvitesPage() {
  // Fail-loud: admin pages have no graceful degradation surface; a missing env var
  // is a deploy misconfiguration, not a runtime fallback condition. Throwing here
  // (inside the component body, not at module top-level) ensures the throw fires
  // only when an admin actually navigates to /admin/beta-invites — not at app boot
  // for every user. BetaInvitesPage is statically imported from App.tsx and Vite
  // constant-folds import.meta.env at build time, so a module-level throw would
  // be unconditional and crash the entire app for any deploy that forgot the env var.
  const rawAppBaseUrl = import.meta.env.VITE_APP_BASE_URL;
  if (!rawAppBaseUrl) {
    throw new Error('VITE_APP_BASE_URL is required but not set');
  }
  const inviteUrlFor = (id: string): string => `${rawAppBaseUrl}/invite/${id}`;
  const navigate = useNavigate();

  const [rows, setRows] = useState<AdminInviteRow[]>([]);
  const [rowsLoading, setRowsLoading] = useState(true);
  const [rowsError, setRowsError] = useState<string | null>(null);

  const [email, setEmail] = useState('');
  const [formState, setFormState] = useState<AdminFormState>('idle');
  const [formError, setFormError] = useState<string | null>(null);
  const [lastInviteUrl, setLastInviteUrl] = useState<string | null>(null);

  const loadRows = useCallback(async () => {
    setRowsLoading(true);
    setRowsError(null);
    // RULE-225: admin reads must go through the JWT-gated Edge Function. The
    // user-facing RLS on `beta_invites` excludes rows redeemed by other users,
    // which would silently hide most invites from the admin table.
    const { data, error } = await supabase.functions.invoke<RawBetaInviteRow[]>(
      'list-beta-invites',
    );

    if (error) {
      console.error('[BetaInvitesPage] failed to load invites', error);
      setRowsError(error.message);
      setRowsLoading(false);
      return;
    }

    const nowMs = Date.now();
    // The Edge Function always returns an array (never null), but tolerate
    // undefined defensively for shape variance across @supabase/supabase-js.
    const raw = data ?? [];
    setRows(raw.map((r) => mapRow(r, nowMs)));
    setRowsLoading(false);
  }, []);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- async load dispatched from effect body; setState is inside callback
    void loadRows();
  }, [loadRows]);

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const trimmed = email.trim();
    if (trimmed.length === 0) return;

    setFormState('submitting');
    setFormError(null);
    setLastInviteUrl(null);

    const body: GenerateInviteRequest = { email: trimmed };
    const { data, error } = await supabase.functions.invoke<GenerateInviteResponse>(
      'generate-beta-invite',
      { body },
    );

    // The Supabase client surfaces non-2xx responses as `error`. We need to
    // distinguish the 409 already-redeemed case so the operator gets actionable
    // copy. The Edge Function returns `{ error: 'already_redeemed' }` on 409.
    if (error) {
      // FunctionsHttpError exposes `context.response` for status inspection.
      // Use a defensive read so we don't crash on shape variance between
      // @supabase/supabase-js minor versions.
      const status = readResponseStatus(error);
      const message =
        status === 409
          ? 'This email has already redeemed a beta invite'
          : error.message || 'Failed to generate invite';
      setFormError(message);
      setFormState('error');
      return;
    }

    if (!data) {
      setFormError('Generate-invite returned no data');
      setFormState('error');
      return;
    }

    setLastInviteUrl(data.inviteUrl);
    setFormState('success');
    setEmail('');
    await loadRows();
  }

  return (
    <div className="flex flex-col gap-6 py-6 px-4" data-testid="beta-invites-page">
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => navigate(-1)}
          aria-label="Go back"
          data-testid="back-button"
          className="flex items-center justify-center -ml-1 p-1 rounded text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] transition-colors"
        >
          <ChevronLeft size={20} />
        </button>
        <h1 className="font-display text-display-sm text-[var(--color-text-display)]">
          Beta Invites
        </h1>
      </div>

      {/* Generate invite form ─ data-admin-form-state is a static literal
          per branch (no `data-admin-form-state={dynamic}` interpolation). */}
      {formState === 'idle' && (
        <form
          onSubmit={handleSubmit}
          data-admin-form-state="idle"
          data-testid="generate-invite-form"
          className="bg-bark-800 border-[0.5px] border-bark-600 rounded-lg p-4 shadow-card flex flex-col gap-3"
        >
          <FormFields email={email} onEmailChange={setEmail} disabled={false} />
          <SubmitButton label="Generate invite" disabled={false} />
        </form>
      )}
      {formState === 'submitting' && (
        <form
          onSubmit={handleSubmit}
          data-admin-form-state="submitting"
          data-testid="generate-invite-form"
          className="bg-bark-800 border-[0.5px] border-bark-600 rounded-lg p-4 shadow-card flex flex-col gap-3"
        >
          <FormFields email={email} onEmailChange={setEmail} disabled={true} />
          <SubmitButton label="Generating…" disabled={true} />
        </form>
      )}
      {formState === 'success' && (
        <form
          onSubmit={handleSubmit}
          data-admin-form-state="success"
          data-testid="generate-invite-form"
          className="bg-bark-800 border-[0.5px] border-bark-600 rounded-lg p-4 shadow-card flex flex-col gap-3"
        >
          <FormFields email={email} onEmailChange={setEmail} disabled={false} />
          <SubmitButton label="Generate another" disabled={false} />
          <div data-testid="generate-success" className="flex flex-col gap-2">
            <p className="text-body-sm text-[var(--color-text-muted)]">
              Invite created. Share this link:
            </p>
            <code
              data-testid="generated-invite-url"
              className="block w-full break-all rounded bg-bark-900 px-3 py-2 text-body-sm text-[var(--color-text-primary)]"
            >
              {lastInviteUrl}
            </code>
          </div>
        </form>
      )}
      {formState === 'error' && (
        <form
          onSubmit={handleSubmit}
          data-admin-form-state="error"
          data-testid="generate-invite-form"
          className="bg-bark-800 border-[0.5px] border-bark-600 rounded-lg p-4 shadow-card flex flex-col gap-3"
        >
          <FormFields email={email} onEmailChange={setEmail} disabled={false} />
          <SubmitButton label="Try again" disabled={false} />
          <p data-testid="generate-error" className="text-body-sm text-ember-400">
            {formError ?? 'Something went wrong'}
          </p>
        </form>
      )}

      {/* Invite table */}
      <section className="flex flex-col gap-3">
        <p className="field-label">All invites</p>

        {rowsLoading && (
          <p data-testid="invites-loading" className="text-body-sm text-[var(--color-text-muted)]">
            Loading invites…
          </p>
        )}

        {!rowsLoading && rowsError && (
          <p data-testid="invites-error" className="text-body-sm text-ember-400">
            {rowsError}
          </p>
        )}

        {!rowsLoading && !rowsError && rows.length === 0 && (
          <p data-testid="invites-empty" className="text-body-sm text-[var(--color-text-muted)]">
            No invites yet.
          </p>
        )}

        {!rowsLoading && !rowsError && rows.length > 0 && (
          <div className="overflow-x-auto rounded-lg bg-bark-800 border-[0.5px] border-bark-600">
            <table className="w-full text-left">
              <thead>
                <tr className="text-body-xs uppercase tracking-wider text-[var(--color-text-muted)]">
                  <th className="px-3 py-2">Email</th>
                  <th className="px-3 py-2">Created</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2">Link</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <InviteRow key={row.id} row={row} inviteUrlFor={inviteUrlFor} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

// ── Subcomponents ────────────────────────────────────────────────────────────

interface FormFieldsProps {
  email: string;
  onEmailChange: (next: string) => void;
  disabled: boolean;
}

function FormFields({ email, onEmailChange, disabled }: FormFieldsProps) {
  return (
    <label className="flex flex-col gap-2">
      <span className="field-label">Recipient email</span>
      <input
        type="email"
        required
        value={email}
        onChange={(e) => onEmailChange(e.target.value)}
        data-testid="generate-invite-email-input"
        className="rounded-md bg-bark-900 px-3 py-2 text-body-md text-[var(--color-text-primary)] placeholder:text-[var(--color-text-placeholder)]"
        placeholder="hunter@example.com"
        disabled={disabled}
      />
    </label>
  );
}

interface SubmitButtonProps {
  label: string;
  disabled: boolean;
}

function SubmitButton({ label, disabled }: SubmitButtonProps) {
  return (
    <button
      type="submit"
      disabled={disabled}
      data-testid="generate-invite-submit"
      className="bg-ember-500 text-white rounded-lg py-3 text-body-md font-semibold transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
    >
      {label}
    </button>
  );
}

interface InviteRowProps {
  row: AdminInviteRow;
  inviteUrlFor: (id: string) => string;
}

/**
 * InviteRow renders a `<tr>` with a static literal `data-admin-row-status`
 * per branch. There is no `data-admin-row-status={row.status}` interpolation —
 * each status case is its own JSX branch with the literal hard-coded.
 *
 * Per consensus-reviewer C7, the literal values are 'sent' | 'active' | 'expired'
 * (was 'pending' | 'redeemed' | 'expired'). The 'expired' value tracks the
 * 90-day beta tier lifecycle, not the 48h invite TTL.
 */
function InviteRow({ row, inviteUrlFor }: InviteRowProps) {
  if (row.status === 'sent') {
    return (
      <tr
        data-admin-row-status="sent"
        data-testid="invite-row"
        className="border-t border-bark-600"
      >
        <RowCells row={row} inviteUrlFor={inviteUrlFor} />
      </tr>
    );
  }
  if (row.status === 'active') {
    return (
      <tr
        data-admin-row-status="active"
        data-testid="invite-row"
        className="border-t border-bark-600"
      >
        <RowCells row={row} inviteUrlFor={inviteUrlFor} />
      </tr>
    );
  }
  // status === 'expired'
  return (
    <tr
      data-admin-row-status="expired"
      data-testid="invite-row"
      className="border-t border-bark-600"
    >
      <RowCells row={row} inviteUrlFor={inviteUrlFor} />
    </tr>
  );
}

function RowCells({ row, inviteUrlFor }: { row: AdminInviteRow; inviteUrlFor: (id: string) => string }) {
  const url = inviteUrlFor(row.id);
  return (
    <>
      <td className="px-3 py-2 text-body-sm text-[var(--color-text-primary)]">{row.email}</td>
      <td className="px-3 py-2 text-body-sm text-[var(--color-text-muted)]">
        {new Date(row.createdAt).toLocaleString()}
      </td>
      <td className="px-3 py-2 text-body-sm capitalize text-[var(--color-text-primary)]">
        {row.status}
      </td>
      <td className="px-3 py-2 text-body-sm">
        <button
          type="button"
          data-testid="copy-invite-url"
          data-invite-url={url}
          onClick={() => {
            void navigator.clipboard.writeText(url).catch((err) => {
              console.error('[BetaInvitesPage] clipboard write failed', err);
            });
          }}
          className="text-ember-400 hover:underline"
        >
          Copy link
        </button>
      </td>
    </>
  );
}

// ── Helpers ──────────────────────────────────────────────────────────────────

interface MaybeFunctionsHttpError {
  context?: { response?: { status?: number } };
}

/**
 * Extract HTTP status from a Supabase Functions error if present.
 *
 * `FunctionsHttpError` from @supabase/supabase-js exposes `context.response`,
 * but the public type surface is narrow. Read defensively so we can distinguish
 * the 409 already-redeemed case without coupling to internal types.
 */
function readResponseStatus(err: unknown): number | undefined {
  if (err == null || typeof err !== 'object') return undefined;
  const ctx = (err as MaybeFunctionsHttpError).context;
  return ctx?.response?.status;
}
